import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import {
  MAKANAN, organScript, planetScript, sunScript, orbitSpeed,
  type Makanan, type OrganId, type Planet,
} from "@jomcerdik/shared";
import { useSains } from "./sainsLang";
import { t } from "../../../lib/i18n";
import { Screen, StudyGate, TopBar } from "../kit";

/* Interactive Sains (DSKP KSSR Semakan 2017, Sains Tahun 3): Perjalanan Makanan (3.3.1, 3.3.2) and Sistem Suria (9.1.1–9.1.3).
   Everything is drawn as SVG, so there are no image files and no cost. The explanations and quizzes are Bahasa Melayu or English (useSains), following the app language. */

import { GHOST, ListenButton, MiniQuiz, NEXT, QUIZ_ROUNDS, reduced, webglOk, type QuizItem } from "./scienceKit";

// three.js is only downloaded when a 3D game is opened
const Digestive3D = lazy(() => import("./Digestive3D"));
const Solar3D = lazy(() => import("./Solar3D"));
const Loading3D = ({ ratio }: { ratio: string }) => <div className={`grid w-full place-items-center rounded-3xl bg-slate-900 text-sm font-bold text-slate-300 ${ratio}`}>3D…</div>;

/** Smoothly animated SVG viewBox ("zoom"). */
function useViewBox(initial: readonly number[]) {
  const [vb, setVb] = useState<number[]>([...initial]);
  const cur = useRef<number[]>([...initial]);
  const raf = useRef(0);
  const to = useCallback((target: readonly number[], ms = 650) => {
    cancelAnimationFrame(raf.current);
    if (reduced() || ms <= 0) { cur.current = [...target]; setVb([...target]); return; }
    const from = [...cur.current];
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      const e = 1 - Math.pow(1 - k, 3);
      const v = from.map((f, i) => f + (target[i]! - f) * e);
      cur.current = v;
      setVb(v);
      if (k < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  return [vb, to] as const;
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  Perjalanan Makanan — sistem pencernaan, x-ray view
 * ═══════════════════════════════════════════════════════════════════════════ */
const BODY_FULL = [0, 0, 300, 440] as const;
/** Where the food sits in each organ, and the box to zoom into. */
const ORGAN_GEO: Record<OrganId, { at: [number, number]; box: [number, number, number, number]; label: [number, number] }> = {
  mulut: { at: [150, 62], box: [105, 28, 90, 70], label: [196, 56] },
  esofagus: { at: [150, 112], box: [105, 78, 90, 90], label: [166, 108] },
  perut: { at: [188, 200], box: [118, 128, 130, 130], label: [230, 188] },
  "usus-kecil": { at: [150, 292], box: [82, 238, 136, 108], label: [8, 292] },
  "usus-besar": { at: [200, 300], box: [72, 232, 156, 156], label: [210, 262] },
  dubur: { at: [150, 394], box: [108, 352, 84, 72], label: [166, 412] },
};
const PERUT_PATH = "M148 146 C185 138 228 160 222 205 C216 245 170 252 150 228 C138 212 160 205 150 190 C144 175 140 160 148 146 Z";
const BESAR_PATH = "M100 342 V248 H200 V340 Q200 356 176 362 L150 374";
const KECIL_PATH = "M122 262 H178 Q190 262 190 272 Q190 282 178 282 H122 Q110 282 110 292 Q110 302 122 302 H178 Q190 302 190 312 Q190 322 178 322 H122 Q110 322 110 330";

function Body({ active, zoom, food, idx, onPick }: { active: OrganId | null; zoom: OrganId | null; food: Makanan | null; idx: number; onPick: (id: OrganId) => void }) {
  const { organs: ORGANS } = useSains();
  const glow = (id: OrganId) => (active === id || zoom === id ? "drop-shadow(0 0 5px #7df9ff)" : "none");
  const dot = idx < 0 ? [150, 14] : ORGAN_GEO[ORGANS[idx]!.id].at;
  const hit = (id: OrganId) => ({
    onClick: () => onPick(id), role: "button" as const, tabIndex: 0, "aria-label": ORGANS.find((o) => o.id === id)!.name,
    onKeyDown: (e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(id); } },
    style: { cursor: "pointer", filter: glow(id) } as CSSProperties,
  });
  const zoomed = zoom !== null;
  return (
    <>
      <defs>
        <linearGradient id="px-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#04233a" /><stop offset="1" stopColor="#0a5478" /></linearGradient>
        <linearGradient id="px-body" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1d6f9c" stopOpacity=".55" /><stop offset="1" stopColor="#0f4468" stopOpacity=".55" /></linearGradient>
      </defs>
      <rect x="-200" y="-200" width="700" height="840" fill="url(#px-bg)" />
      {/* faint scan lines for the x-ray look */}
      {Array.from({ length: 22 }, (_, i) => <line key={i} x1="-200" x2="500" y1={i * 22 - 20} y2={i * 22 - 20} stroke="#7df9ff" strokeOpacity=".05" />)}
      {/* body silhouette */}
      <g fill="url(#px-body)" stroke="#7df9ff" strokeOpacity=".7" strokeWidth="2">
        <circle cx="150" cy="52" r="40" />
        <rect x="136" y="88" width="28" height="30" rx="8" />
        <path d="M96 112 Q58 120 54 190 L48 336 Q52 404 100 424 L200 424 Q248 404 252 336 L246 190 Q242 120 204 112 Z" />
        <path d="M54 130 Q22 190 28 300" fill="none" strokeWidth="14" strokeLinecap="round" strokeOpacity=".45" />
        <path d="M246 130 Q278 190 272 300" fill="none" strokeWidth="14" strokeLinecap="round" strokeOpacity=".45" />
      </g>
      {/* usus besar (behind usus kecil) */}
      <g {...hit("usus-besar")}>
        <path d={BESAR_PATH} fill="none" stroke="#c98a5b" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" />
        <path d={BESAR_PATH} fill="none" stroke="#e2b088" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 7" />
        <path d={BESAR_PATH} fill="none" stroke="transparent" strokeWidth="30" />
      </g>
      {/* esofagus */}
      <g {...hit("esofagus")}>
        <path d="M150 82 V150" stroke="#e57c96" strokeWidth="13" strokeLinecap="round" />
        <path d="M150 82 V150" stroke="#f6b3c3" strokeWidth="4" strokeLinecap="round" />
        <path d="M150 82 V150" stroke="transparent" strokeWidth="30" />
      </g>
      {/* perut */}
      <g {...hit("perut")}>
        <path d={PERUT_PATH} fill="#d6487f" stroke="#ff9ec0" strokeWidth="3" />
        <path d="M165 165 C195 160 210 185 205 205" fill="none" stroke="#ff9ec0" strokeOpacity=".7" strokeWidth="3" strokeLinecap="round" />
      </g>
      {/* usus kecil */}
      <g {...hit("usus-kecil")}>
        <path d={KECIL_PATH} fill="none" stroke="#f08a5d" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
        <path d={KECIL_PATH} fill="none" stroke="#ffc7a8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d={KECIL_PATH} fill="none" stroke="transparent" strokeWidth="26" />
      </g>
      {/* dubur */}
      <g {...hit("dubur")}>
        <path d="M150 374 V398" stroke="#a96b44" strokeWidth="14" strokeLinecap="round" />
        <circle cx="150" cy="402" r="7" fill="#7a4a2d" stroke="#e2b088" strokeWidth="2" />
      </g>
      {/* mulut */}
      <g {...hit("mulut")}>
        <ellipse cx="150" cy="62" rx="22" ry="11" fill="#c63b63" stroke="#ff9ec0" strokeWidth="2" />
        <rect x="132" y="53" width="36" height="6" rx="2" fill="#fff" />
        <rect x="132" y="66" width="36" height="5" rx="2" fill="#fff" />
        <ellipse cx="150" cy="63" rx="8" ry="3.5" fill="#ff8fb0" />
      </g>

      {/* zoom-only details */}
      {zoom === "perut" && (
        <g>
          {Array.from({ length: 9 }, (_, i) => (
            <circle key={i} cx={160 + ((i * 17) % 52)} cy={180 + ((i * 29) % 52)} r="3.2" fill="#9be15d" stroke="#e6ffb8" strokeWidth=".8">
              <animate attributeName="cy" values={`${180 + ((i * 29) % 52)};${174 + ((i * 29) % 52)};${180 + ((i * 29) % 52)}`} dur={`${2 + (i % 3) * 0.6}s`} repeatCount="indefinite" />
            </circle>
          ))}
          <text x="196" y="241" fontSize="9" fontWeight="800" fill="#e6ffb8" stroke="#0a3f1a" strokeWidth="2" paintOrder="stroke">ASID</text>
        </g>
      )}
      {zoom === "usus-kecil" && (
        <g>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <circle key={i} cx={124 + i * 11} cy={i % 2 ? 258 : 306} r="2.6" fill="#ff4d5e" stroke="#fff" strokeWidth=".6">
              <animate attributeName="r" values="2;3.4;2" dur="1.4s" begin={`${i * 0.2}s`} repeatCount="indefinite" />
            </circle>
          ))}
          <text x="84" y="344" fontSize="8" fontWeight="800" fill="#ffd9c5" stroke="#4a1b05" strokeWidth="2" paintOrder="stroke">Zat makanan → darah</text>
        </g>
      )}
      {zoom === "usus-besar" && (
        <g>
          {[0, 1, 2].map((i) => (
            <path key={i} d={`M${112 + i * 36} ${252 + i * 2} q-4 7 0 10 q4 -3 0 -10z`} fill="#6ec3ff" stroke="#e6f6ff" strokeWidth=".6" />
          ))}
          <text x="104" y="372" fontSize="8" fontWeight="800" fill="#e6f6ff" stroke="#06314f" strokeWidth="2" paintOrder="stroke">Air diserap</text>
        </g>
      )}

      {/* labels (full view only) */}
      {!zoomed && ORGANS.map((o) => {
        const [lx, ly] = ORGAN_GEO[o.id].label;
        const [ax, ay] = ORGAN_GEO[o.id].at;
        return (
          <g key={o.id} pointerEvents="none">
            <line x1={ax} y1={ay} x2={lx} y2={ly - 3} stroke="#cdf6ff" strokeOpacity=".6" strokeWidth=".8" />
            <text x={lx + 2} y={ly} fontSize="10" fontWeight="800" fill="#e8fbff" stroke="#04233a" strokeWidth="2.2" paintOrder="stroke">{o.name}</text>
          </g>
        );
      })}

      {/* the food being eaten */}
      {food && (
        <g style={{ transform: `translate(${dot[0]}px, ${dot[1]}px)`, transition: reduced() ? "none" : "transform 1.1s cubic-bezier(.4,.1,.2,1)" }} pointerEvents="none">
          <circle r="8" fill={food.colour} stroke="#fff" strokeWidth="2" />
          <circle r="3" cx="-2.5" cy="-2.5" fill="#fff" fillOpacity=".6" />
        </g>
      )}
    </>
  );
}


export function PencernaanPage() {
  return <Screen><StudyGate><Pencernaan /></StudyGate></Screen>;
}

const PENC_DETAILS = { game: "sains-pencernaan" };

function Pencernaan() {
  const { organs: ORGANS, lang, food: foodLabel, pencernaan } = useSains();
  const organLabel = (id: OrganId | null) => (id ? ORGANS.find((o) => o.id === id)! : null);
  const [food, setFood] = useState<Makanan>(MAKANAN[0]!);
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(-1);
  const [zoom, setZoom] = useState<OrganId | null>(null);
  const [quiz, setQuiz] = useState(false);
  const [vb, zoomTo] = useViewBox(BODY_FULL);
  const [flat, setFlat] = useState(() => !webglOk());
  const items = useMemo<QuizItem[]>(() => pencernaan(QUIZ_ROUNDS).map(({ prompt, options, answer }) => ({ prompt, options, answer })), [quiz, lang]);

  const focusOrgan = (id: OrganId | null) => {
    setZoom(id);
    if (flat) zoomTo(id ? ORGAN_GEO[id].box : BODY_FULL);
  };
  const eat = () => { setStarted(true); setIdx(0); };
  const next = () => {
    const n = Math.min(idx + 1, ORGANS.length - 1);
    setIdx(n);
    if (zoom) focusOrgan(ORGANS[n]!.id);
  };
  const cur = organLabel(zoom) ?? (idx >= 0 ? ORGANS[idx]! : null);
  const finished = idx === ORGANS.length - 1;
  const text = cur ? cur.detail : t("Pilih makanan, kemudian tekan Makan!");

  if (quiz) {
    return (
      <>
        <TopBar back="/belajar/permainan" title={t("Perjalanan Makanan")} subtitle={t("Kuiz")} />
        <MiniQuiz items={items} details={PENC_DETAILS} onExit={() => setQuiz(false)} />
      </>
    );
  }
  return (
    <>
      <TopBar back="/belajar/permainan" title={t("Perjalanan Makanan")} subtitle={t("Ikut makanan masuk ke dalam badan. Tekan organ untuk zum.")} />
      <div className="mx-auto max-w-md space-y-3">
        {flat ? (
          <div className="overflow-hidden rounded-3xl bg-slate-900 shadow-lg ring-1 ring-black/10">
            <svg viewBox={vb.join(" ")} className="block aspect-[300/440] w-full" role="img" aria-label={t("Sistem pencernaan manusia")}>
              <Body active={idx >= 0 ? ORGANS[idx]!.id : null} zoom={zoom} food={started ? food : null} idx={idx} onPick={focusOrgan} />
            </svg>
          </div>
        ) : (
          <Suspense fallback={<Loading3D ratio="aspect-[3/4]" />}>
            <Digestive3D started={started} foodId={food.id} idx={idx} zoom={zoom} onPick={focusOrgan} onFail={() => setFlat(true)} />
          </Suspense>
        )}

        <div className="rounded-3xl bg-card p-4 shadow-sm ring-1 ring-black/5">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              {cur && <p className="text-lg font-black text-slate-900">{cur.name}</p>}
              <p className="text-sm leading-relaxed text-slate-700" aria-live="polite">{text}</p>
            </div>
            <ListenButton lines={cur ? organScript(cur) : [text]} />
          </div>
          {zoom === "perut" && <p className="mt-2 rounded-xl bg-lime-50 px-3 py-2 text-xs font-bold text-lime-800">{t("Titik hijau ialah asid dalam perut.")}</p>}
        </div>

        {!started ? (
          <div className="space-y-3">
            <div className="flex flex-wrap justify-center gap-2" role="group" aria-label={t("Pilih makanan")}>
              {MAKANAN.map((m) => (
                <button key={m.id} type="button" aria-pressed={food.id === m.id} onClick={() => setFood(m)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ring-1 ${food.id === m.id ? "bg-slate-900 text-white ring-slate-900" : "bg-card text-slate-700 ring-black/10"}`}>
                  <span className="h-4 w-4 rounded-full ring-2 ring-white" style={{ background: m.colour }} />{foodLabel(m.id, m.name)}
                </button>
              ))}
            </div>
            <button type="button" onClick={eat} className={`${NEXT} w-full py-3.5 text-lg`}>{t("Makan!")}</button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {zoom && <button type="button" onClick={() => focusOrgan(null)} className={GHOST}>{t("Zum keluar")}</button>}
            {!finished && <button type="button" onClick={next} className={NEXT}>{t("Seterusnya")} ▶</button>}
            {finished && <button type="button" onClick={() => setQuiz(true)} className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-extrabold text-white shadow-md">{t("Kuiz")}</button>}
            <button type="button" onClick={() => { setStarted(false); setIdx(-1); focusOrgan(null); }} className={GHOST}>{t("Makan lagi")}</button>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-1.5" role="group" aria-label={t("Organ")}>
          {ORGANS.map((o, i) => (
            <button key={o.id} type="button" onClick={() => focusOrgan(o.id)}
              className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ${zoom === o.id ? "bg-cyan-600 text-white ring-cyan-700" : i <= idx ? "bg-cyan-50 text-cyan-800 ring-cyan-200" : "bg-card text-slate-600 ring-black/10"}`}>
              {i + 1}. {o.name}
            </button>
          ))}
        </div>
        {started && finished && <p className="text-center text-xs text-slate-500">{t("Makanan yang tidak diperlukan oleh badan keluar sebagai najis.")}</p>}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  Sistem Suria — orbiting planets, tap to zoom
 * ═══════════════════════════════════════════════════════════════════════════ */
const SUN_R = 17;
const PLANET_K = 0.9;
const ORBIT_R = [30, 44, 59, 75, 100, 125, 149, 172];
const SOLAR_FULL = [-190, -190, 380, 380] as const;
const START_ANGLE = [0.4, 2.1, 3.6, 5.2, 1.2, 4.1, 5.9, 2.8];

function stars(n: number) {
  return Array.from({ length: n }, (_, i) => {
    const a = Math.sin(i * 91.7) * 10000, b = Math.sin(i * 47.3 + 5) * 10000;
    return { x: (a - Math.floor(a)) * 460 - 230, y: (b - Math.floor(b)) * 460 - 230, r: 0.4 + ((i * 7) % 5) * 0.2 };
  });
}
const STARS = stars(90);

function PlanetBall({ p, r }: { p: { id: string; colour: string; colour2: string; rings?: boolean }; r: number }) {
  const gid = `pb-${p.id}`;
  return (
    <g>
      <defs>
        <radialGradient id={gid} cx=".35" cy=".3" r=".85"><stop offset="0" stopColor={p.colour} /><stop offset="1" stopColor={p.colour2} /></radialGradient>
        <clipPath id={`${gid}-c`}><circle r={r} /></clipPath>
      </defs>
      {p.rings && <ellipse rx={r * 1.95} ry={r * 0.5} fill="none" stroke="#d9c58d" strokeWidth={r * 0.22} strokeOpacity=".85" transform="rotate(-14)" />}
      <circle r={r} fill={`url(#${gid})`} />
      <g clipPath={`url(#${gid}-c)`}>
        {(p.id === "musytari" || p.id === "zuhal") && [-0.55, -0.2, 0.15, 0.5].map((y, i) => (
          <rect key={i} x={-r} y={y * r} width={r * 2} height={r * (i % 2 ? 0.18 : 0.26)} fill={p.colour2} fillOpacity=".45" />
        ))}
        {p.id === "bumi" && (<>
          <path d={`M${-r * 0.5} ${-r * 0.2} q${r * 0.3} ${-r * 0.5} ${r * 0.6} ${-r * 0.2} q${r * 0.1} ${r * 0.5} ${-r * 0.3} ${r * 0.6} q${-r * 0.4} ${-r * 0.05} ${-r * 0.3} ${-r * 0.4}z`} fill="#3f9b57" />
          <path d={`M${r * 0.2} ${r * 0.3} q${r * 0.4} ${-r * 0.1} ${r * 0.5} ${r * 0.2} q${-r * 0.2} ${r * 0.4} ${-r * 0.55} ${r * 0.2}z`} fill="#3f9b57" />
          <ellipse cx="0" cy={-r * 0.95} rx={r * 0.5} ry={r * 0.18} fill="#fff" fillOpacity=".85" />
        </>)}
        {p.id === "marikh" && <ellipse cx="0" cy={-r * 0.95} rx={r * 0.45} ry={r * 0.2} fill="#fff" fillOpacity=".9" />}
        {p.id === "zuhrah" && [-0.4, 0, 0.4].map((y, i) => <rect key={i} x={-r} y={y * r} width={r * 2} height={r * 0.14} fill="#fff" fillOpacity=".25" />)}
        {p.id === "utarid" && [[-0.4, -0.2, 0.18], [0.3, 0.3, 0.22], [0.1, -0.5, 0.12]].map(([x, y, k], i) => <circle key={i} cx={x! * r} cy={y! * r} r={k! * r} fill={p.colour2} fillOpacity=".55" />)}
      </g>
      {p.rings && <path d={`M${-r * 1.95} 0 A${r * 1.95} ${r * 0.5} 0 0 0 ${r * 1.95} 0`} fill="none" stroke="#d9c58d" strokeWidth={r * 0.22} strokeOpacity=".95" transform="rotate(-14)" />}
    </g>
  );
}

const SUN_BALL = { id: "matahari", colour: "#fff2a8", colour2: "#f59e0b" } as const;

type Sel = "matahari" | Planet["id"] | null;
const SURIA_DETAILS = { game: "sains-suria" };

export function SistemSuriaPage() {
  return <Screen><StudyGate><SistemSuria /></StudyGate></Screen>;
}

function SistemSuria() {
  const { planets: PLANETS, sun, rule, days, suria, lang } = useSains();
  const [sel, setSel] = useState<Sel>(null);
  const [paused, setPaused] = useState(false);
  const [quiz, setQuiz] = useState(false);
  const [vb, zoomTo] = useViewBox(SOLAR_FULL);
  const [flat, setFlat] = useState(() => !webglOk());
  const nodes = useRef<(SVGGElement | null)[]>([]);
  const angles = useRef([...START_ANGLE]);
  const frozen = useRef(false);
  const timer = useRef(0);
  frozen.current = paused || sel !== null;

  useEffect(() => {
    if (!flat) return;
    let last = performance.now();
    let id = 0;
    const draw = () => PLANETS.forEach((_, i) => {
      const a = angles.current[i]!;
      nodes.current[i]?.setAttribute("transform", `translate(${(ORBIT_R[i]! * Math.cos(a)).toFixed(2)} ${(ORBIT_R[i]! * Math.sin(a)).toFixed(2)})`);
    });
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (!frozen.current && !reduced()) PLANETS.forEach((p, i) => { angles.current[i] = angles.current[i]! + orbitSpeed(p.orbitDays) * dt; });
      draw();
      id = requestAnimationFrame(tick);
    };
    draw();
    id = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(id); window.clearTimeout(timer.current); };
  }, [flat]);

  const items = useMemo<QuizItem[]>(() => suria(QUIZ_ROUNDS).map(({ prompt, options, answer }) => ({ prompt, options, answer })), [quiz, lang]);

  const open = (s: Exclude<Sel, null>) => {
    window.clearTimeout(timer.current);
    let box: number[] = [-30, -30, 60, 60];
    const i = PLANETS.findIndex((p) => p.id === s);
    if (i >= 0) {
      const a = angles.current[i]!;
      const x = ORBIT_R[i]! * Math.cos(a), y = ORBIT_R[i]! * Math.sin(a);
      const w = Math.max(36, PLANETS[i]!.size * 3);
      box = [x - w / 2, y - w / 2, w, w];
    }
    setPaused(true);
    if (!flat) { setSel(s); return; }
    zoomTo(box, 600);
    timer.current = window.setTimeout(() => setSel(s), reduced() ? 0 : 520);
  };
  const close = () => {
    window.clearTimeout(timer.current);
    setSel(null);
    if (flat) zoomTo(SOLAR_FULL, 600);
    setPaused(false);
  };

  if (quiz) {
    return (
      <>
        <TopBar back="/belajar/permainan" title={t("Sistem Suria")} subtitle={t("Kuiz")} />
        <MiniQuiz items={items} details={SURIA_DETAILS} onExit={() => setQuiz(false)} />
      </>
    );
  }

  const planet = PLANETS.find((p) => p.id === sel) ?? null;
  const pIndex = planet ? PLANETS.indexOf(planet) : -1;
  const goto = (d: number) => { const n = (pIndex + d + PLANETS.length) % PLANETS.length; setSel(PLANETS[n]!.id); };

  /** Text, facts and previous/next buttons for the selected body (shared by the 2D overlay and the 3D bottom sheet). */
  const info = sel && (
    <>
      <h2 className="text-center text-2xl font-black">{planet ? planet.name : sun.name}</h2>
      <p className="mt-0.5 text-center text-sm font-bold text-amber-300">{planet ? planet.tagline : sun.tagline}.</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-200">{planet ? planet.detail : sun.detail}</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-200">
        {(planet ? planet.facts : sun.facts).map((f) => <li key={f}>{f}</li>)}
      </ul>
      {planet && (
        <dl className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px]">
          <div className="rounded-xl bg-white/10 p-2"><dt className="text-slate-300">{t("Kedudukan")}</dt><dd className="mt-0.5 text-sm font-extrabold">{pIndex + 1} / 8</dd></div>
          <div className="rounded-xl bg-white/10 p-2"><dt className="text-slate-300">{t("Satu edaran")}</dt><dd className="mt-0.5 text-sm font-extrabold">{days(planet.orbitDays)}</dd></div>
          <div className="rounded-xl bg-white/10 p-2"><dt className="text-slate-300">{t("Suhu")}</dt><dd className="mt-0.5 text-sm font-extrabold">{planet.tempBand}</dd><dd className="text-[10px] text-slate-300">≈ {planet.tempC}°C</dd></div>
        </dl>
      )}
      {planet && (
        <div className="mt-3 flex justify-between gap-2">
          <button type="button" onClick={() => goto(-1)} className="rounded-full bg-white/15 px-4 py-2 text-xs font-bold">‹ {PLANETS[(pIndex + 7) % 8]!.name}</button>
          <button type="button" onClick={() => goto(1)} className="rounded-full bg-white/15 px-4 py-2 text-xs font-bold">{PLANETS[(pIndex + 1) % 8]!.name} ›</button>
        </div>
      )}
    </>
  );
  const heard = planet ? planetScript(planet) : sunScript(lang);

  return (
    <>
      <TopBar back="/belajar/permainan" title={t("Sistem Suria")} subtitle={t("Planet mengelilingi Matahari. Tekan planet untuk zum.")} />
      <div className="mx-auto max-w-md space-y-3">
        {flat ? (
          <div className="relative overflow-hidden rounded-3xl bg-[#050816] shadow-lg ring-1 ring-black/10">
            <svg viewBox={vb.join(" ")} className="block aspect-square w-full" role="img" aria-label={t("Sistem Suria")}>
              <defs>
                <radialGradient id="sun-glow"><stop offset="0" stopColor="#ffe27a" stopOpacity=".9" /><stop offset=".5" stopColor="#ff9d2e" stopOpacity=".35" /><stop offset="1" stopColor="#ff7a00" stopOpacity="0" /></radialGradient>
              </defs>
              <rect x="-260" y="-260" width="520" height="520" fill="#050816" />
              {STARS.map((s, i) => <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#fff" fillOpacity=".7" />)}
              {ORBIT_R.map((r) => <circle key={r} r={r} fill="none" stroke="#9fb7ff" strokeOpacity=".22" strokeDasharray="2 3" strokeWidth=".6" />)}
              <g onClick={() => open("matahari")} role="button" tabIndex={0} aria-label={sun.name} style={{ cursor: "pointer" }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open("matahari"); } }}>
                <circle r={SUN_R * 2.2} fill="url(#sun-glow)" />
                <PlanetBall p={SUN_BALL} r={SUN_R} />
              </g>
              {PLANETS.map((p, i) => (
                <g key={p.id} ref={(el) => { nodes.current[i] = el; }} onClick={() => open(p.id)} role="button" tabIndex={0} aria-label={p.name} style={{ cursor: "pointer" }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(p.id); } }}>
                  <circle r={Math.max(9, p.size + 5)} fill="transparent" />
                  <PlanetBall p={p} r={p.size * PLANET_K} />
                  <text y={p.size * PLANET_K + 7} textAnchor="middle" fontSize="6.5" fontWeight="800" fill="#e8eeff" stroke="#050816" strokeWidth="1.6" paintOrder="stroke" pointerEvents="none" opacity={vb[2]! > 300 ? 1 : 0}>{p.name}</text>
                </g>
              ))}
            </svg>

            {sel && (
              <div className="absolute inset-0 animate-rise overflow-y-auto bg-[#050816]/95 p-4 text-white" role="dialog" aria-label={planet?.name ?? sun.name}>
                <div className="flex items-center justify-between">
                  <button type="button" onClick={close} className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold">← {t("Zum keluar")}</button>
                  <ListenButton dark lines={heard} />
                </div>
                <svg viewBox="-90 -62 180 124" className="mx-auto mt-1 h-36 w-full">
                  {sel === "matahari" ? <><circle r="60" fill="url(#sun-glow)" /><PlanetBall p={SUN_BALL} r={38} /></> : <PlanetBall p={planet!} r={Math.min(46, 16 + planet!.size * 1.7)} />}
                </svg>
                {info}
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <Suspense fallback={<Loading3D ratio="aspect-[4/5]" />}>
              <Solar3D sel={sel} paused={paused} onPick={open} onFail={() => setFlat(true)} />
            </Suspense>
            {sel && (
              <div className="absolute inset-x-0 bottom-0 max-h-[48%] animate-rise overflow-y-auto rounded-b-3xl rounded-t-3xl bg-[#050816]/90 p-4 text-white backdrop-blur" role="dialog" aria-label={planet?.name ?? "Matahari"}>
                <div className="mb-1 flex items-center justify-between">
                  <button type="button" onClick={close} className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold">← {t("Zum keluar")}</button>
                  <ListenButton dark lines={heard} />
                </div>
                {info}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2">
          <button type="button" onClick={() => setPaused((p) => !p)} disabled={sel !== null} className={GHOST}>{paused ? t("Main") : t("Jeda")}</button>
          <button type="button" onClick={() => setQuiz(true)} className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-extrabold text-white shadow-md">{t("Kuiz")}</button>
        </div>
        <p className="rounded-2xl bg-card p-3 text-center text-xs leading-relaxed text-slate-600 ring-1 ring-black/5">{rule}</p>
      </div>
    </>
  );
}
